import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { INextStep } from '../../modules/ContactManager/components/Header/interfaces';
import { IPullPayOffQuoteResponse } from '../../modules/ContactManager/components/Tabs/interfaces';
import { RootState } from '../store';

interface ICurrentWorkflowStep {
  workflowName?: string;
  workflowStepName?: string;
}

interface TextMessageDictionary {
  [contactId: string]: string;
}
interface IContactManagerInitialState {
  selectedLenderCode: string;
  workflowCurrentStep: ICurrentWorkflowStep | null;
  ChangeWorkflowNextStepTo: string | null; // being used to update workflow from back-end based on CUDL document
  workflowNextStep: INextStep | null;
  lastCommunicationDate: Date | null;
  lastUpdateProductsPlanDate: string | null;
  payoffQuoteResponse: IPullPayOffQuoteResponse | null;
  refreshWorkflow: number;
  lastCommentsRefresh: string;
  dealReCalculate: boolean | null;
  dealSave: boolean | null;
  refreshFiles: string;
  savedTextMessages: TextMessageDictionary | null;

}

const initialState: IContactManagerInitialState = {
  selectedLenderCode: '',
  workflowCurrentStep: {} as ICurrentWorkflowStep,
  workflowNextStep: null,
  ChangeWorkflowNextStepTo: null,
  lastCommunicationDate: null,
  lastUpdateProductsPlanDate: null,
  payoffQuoteResponse: null,
  refreshWorkflow: 0,
  lastCommentsRefresh: new Date().toISOString(),
  dealReCalculate: false,
  dealSave: false,
  refreshFiles: new Date().toISOString(),
  savedTextMessages: {},
};

export const contactManagerSlice = createSlice({
  name: 'contactManager',
  initialState,
  reducers: {
    setSelectedLenderCode: (state, action: PayloadAction<string>) => {
      state.selectedLenderCode = action.payload;
    },
    setWorkflowCurrentStep: (state, action: PayloadAction<ICurrentWorkflowStep | null>) => {
      state.workflowCurrentStep = action.payload;
    },
    setWorkflowNextStep: (state, action: PayloadAction<INextStep>) => {
      state.workflowNextStep = action.payload;
    },
    setChangeWorkflowNextStepTo: (state, action: PayloadAction<string | null>) => {
      state.ChangeWorkflowNextStepTo = action.payload;
    },
    setLastCommunicationDate: (state, action: PayloadAction<Date>) => {
      state.lastCommunicationDate = action.payload;
    },
    setLastUpdateProductsPlanDate: (state, action: PayloadAction<string>) => {
      state.lastUpdateProductsPlanDate = action.payload;
    },
    setPayoffQuoteResponse:(state, action: PayloadAction<IPullPayOffQuoteResponse>) => {
      state.payoffQuoteResponse = action.payload;
    },
    setRefreshWorkflow:(state, action: PayloadAction<number>) => {
      state.refreshWorkflow = action.payload + 1;
    },
    setLastCommentsRefresh:(state, action: PayloadAction<string>) => {
      state.lastCommentsRefresh = action.payload;
    },
    setDealReCalculate:(state, action: PayloadAction<boolean>) => {
      state.dealReCalculate = action.payload;
    },
    setDealSave:(state, action: PayloadAction<boolean>) => {
      state.dealSave = action.payload;
    },
    setRefreshFiles:(state, action: PayloadAction<string>) => {
      state.refreshFiles = action.payload;
    },
    setSavedTextMessageForContact:(state, action: PayloadAction<{ contactId: string; message: string | null }>) => {
      const { contactId, message } = action.payload;
       if (message === null) {
        if (state.savedTextMessages) {
          const { [contactId]: _, ...rest } = state.savedTextMessages;
          state.savedTextMessages = rest;
        }
      } else {
        state.savedTextMessages = {
          ...(state.savedTextMessages || {}),
          [contactId]: message
        };
      }
    }
  },
});

export const {
  setWorkflowCurrentStep,
  setWorkflowNextStep,
  setSelectedLenderCode,
  setLastCommunicationDate,
  setLastUpdateProductsPlanDate,
  setPayoffQuoteResponse,
  setRefreshWorkflow,
  setLastCommentsRefresh,
  setDealReCalculate,
  setChangeWorkflowNextStepTo,
  setDealSave,
  setRefreshFiles,
  setSavedTextMessageForContact
} = contactManagerSlice.actions;

export const selectWorkflowCurrentStep = (state: RootState) => state.contactManager.workflowCurrentStep;
export const selectWorkflowNextStep = (state: RootState) => state.contactManager.workflowNextStep;
export const selectChangeWorkflowNextStepTo = (state: RootState) => state.contactManager.ChangeWorkflowNextStepTo;
export const selectLastCommunicationDate = (state: RootState) => state.contactManager.lastCommunicationDate;
export const selectLastUpdateProductsPlanDate = (state: RootState) => state.contactManager.lastUpdateProductsPlanDate;
export const selectLenderCode = (state: RootState) => state.contactManager.selectedLenderCode;
export const selectPayoffQuoteResponse = (state: RootState) => state.contactManager.payoffQuoteResponse;
export const refreshWorkflow = (state: RootState) => state.contactManager.refreshWorkflow;
export const selectLastCommentsRefresh = (state: RootState) => state.contactManager.lastCommentsRefresh;
export const selectReCalculateDeal = (state: RootState) => state.contactManager.dealReCalculate;
export const selectDealSave = (state: RootState) => state.contactManager.dealSave;
export const selectRefreshFiles = (state: RootState) => state.contactManager.refreshFiles;
export const selectSavedTextMessageForContact = (state: RootState, contactId: string) => state.contactManager.savedTextMessages?.[contactId] || '';